import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { api, type OwnerSessionPolicyView, type OwnerSessionView } from "../api";

/**
 * SIMO-OWNER-SESSION-MANAGEMENT-UI-1 — die Sicherheitsflaeche fuer Sessions.
 *
 * Der Server hat die Routen seit dem Hardening-Block; sichtbar waren sie
 * nirgends. Genau das hat zu 180 nicht zuordenbaren Sessions gefuehrt: was man
 * nicht sieht, raeumt man nicht auf.
 *
 * Zwei Grenzen ziehen sich durch die Datei:
 *
 *  * **Nichts Geheimes.** Token, Token-Hashes und Cookie-Werte kommen vom
 *    Server erst gar nicht — die Ansicht kann sie also nicht versehentlich
 *    zeigen. Auch die rohe Session-ID ist nie die Hauptdarstellung; sie
 *    erscheint nur gekuerzt als Unterscheidungshilfe, wenn zwei Eintraege
 *    sonst gleich aussehen.
 *  * **Der Server entscheidet.** Die Ansicht leitet ihre Moeglichkeiten
 *    ausschliesslich aus der Antwort ab: `current` kommt vom Server, nicht aus
 *    einem lokalen Vergleich. Ein Klick, den der Server ablehnt, bleibt
 *    abgelehnt — die Oberflaeche taeuscht keinen Erfolg vor.
 */

const CHANNEL_LABELS: Record<OwnerSessionView["channel"], string> = {
  web: "Browser",
  mobile: "Mobil-App",
  tablet: "Tablet",
  api: "Programmzugriff",
  maintenance: "Wartungszugang",
  unknown: "Unbekannt",
};

/**
 * Kurzform einer Session-ID — nur als Unterscheidungshilfe.
 *
 * Bewusst nicht die vollstaendige ID: sie ist ein interner Bezeichner und
 * gehoert nicht als primaere Darstellung in die Oberflaeche.
 */
export function shortSessionId(id: string): string {
  return id.slice(0, 8);
}

/** Verstaendliche, absolute Zeitangabe statt „vor 3 Tagen". */
export function formatSessionTime(iso: string): string {
  const value = Date.parse(iso);
  if (!Number.isFinite(value)) return "unbekannt";
  return new Date(value).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export type SessionStatus = "active" | "expired";

/**
 * Der Server liefert nur gueltige Sessions. Eine, deren Ablauf inzwischen
 * erreicht ist, wird trotzdem als abgelaufen gezeigt statt als aktiv — die
 * Liste kann zwischen Abruf und Anzeige altern.
 */
export function sessionStatus(session: OwnerSessionView, now: number = Date.now()): SessionStatus {
  const expires = Date.parse(session.expiresAt);
  if (Number.isFinite(expires) && expires <= now) return "expired";
  return "active";
}

/** Menschliche Beschreibung des Idle-Fensters. */
export function describeIdleWindow(policy: OwnerSessionPolicyView): string {
  const days = policy.idleTimeoutMs / (24 * 60 * 60 * 1000);
  if (days >= 1) {
    const rounded = Math.round(days);
    return `Wird ein Geraet ${rounded} ${rounded === 1 ? "Tag" : "Tage"} nicht benutzt, meldet SIMO es automatisch ab.`;
  }
  const minutes = Math.max(1, Math.round(policy.idleTimeoutMs / 60_000));
  return `Wird ein Geraet ${minutes} Minuten nicht benutzt, meldet SIMO es automatisch ab.`;
}

function StatusBadge({ status, current }: { status: SessionStatus; current: boolean }): JSX.Element {
  if (current) {
    return (
      <span className="inline-flex items-center rounded-full bg-jarvis-accent/20 px-2 py-0.5 text-xs text-jarvis-accent">
        Dieses Geraet
      </span>
    );
  }
  if (status === "expired") {
    return (
      <span className="inline-flex items-center rounded-full bg-jarvis-muted/20 px-2 py-0.5 text-xs text-jarvis-muted">
        Abgelaufen
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-400">
      Aktiv
    </span>
  );
}

export function SettingsSessionsPanel(): JSX.Element {
  const [sessions, setSessions] = useState<OwnerSessionView[]>([]);
  const [policy, setPolicy] = useState<OwnerSessionPolicyView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const meldungRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.ownerSessions();
      setSessions(result.sessions);
      setPolicy(result.policy);
      // SIMO-SESSION-LIST-ERROR-RETRY-1: `error` wird bewusst erst hier,
      // also erst bei Erfolg, geloescht. Wuerde die Zeile schon vor dem
      // Abruf stehen, verschwaende die Fehlermeldung fuer den Moment des
      // Versuchs und kaeme bei einem erneuten Fehlschlag wieder — ein
      // sichtbares Flackern, das den Eindruck erwecken kann, der Fehler sei
      // schon behoben, obwohl der Abruf noch laeuft.
      setError(null);
    } catch {
      // Bewusst keine Rohfehlermeldung: sie kann interne Pfade oder Codes
      // enthalten und hilft dem Owner nicht weiter. Dieselbe Meldung gilt
      // unveraendert auch fuer einen wiederholten Fehlschlag nach "Erneut
      // versuchen" — es entsteht nie ein Rohtext des Servers.
      setError("Die Geraeteliste konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * SIMO-SESSION-MANAGEMENT-VISUAL-QA-1 — die Rueckmeldung muss dorthin, wo
   * geklickt wurde.
   *
   * Meldung und Liste stehen in derselben scrollenden Flaeche, die Meldung
   * aber ganz oben. Im Messstand bei 320x568 lag sie nach einem Klick auf den
   * untersten Knopf 396 px oberhalb des Sichtfelds: der Owner klickt „alle
   * anderen abmelden" und sieht danach nichts — weder Erfolg noch Fehler. Auf
   * einem breiten Bildschirm faellt das nicht auf, weil dort alles gleichzeitig
   * sichtbar ist.
   *
   * `block: "nearest"` scrollt nur, wenn wirklich noetig, und laesst die
   * Ansicht in Ruhe, wenn die Meldung ohnehin schon zu sehen ist.
   */
  useEffect(() => {
    if (!error && !notice) return;
    const element = meldungRef.current;
    // jsdom kennt `scrollIntoView` nicht — der Aufruf bleibt deshalb defensiv,
    // damit die Komponententests nicht an einer Browser-API scheitern.
    if (element && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ block: "nearest" });
    }
  }, [error, notice]);

  const revokeOne = useCallback(
    async (session: OwnerSessionView) => {
      setBusyId(session.id);
      setNotice(null);
      setError(null);
      try {
        await api.revokeOwnerSession(session.id);
        setNotice("Das Geraet wurde abgemeldet.");
        await load();
      } catch {
        setError("Das Geraet konnte nicht abgemeldet werden.");
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const revokeOthers = useCallback(async () => {
    setBusyId("others");
    setNotice(null);
    setError(null);
    try {
      const result = await api.revokeOtherOwnerSessions();
      setNotice(
        result.revoked === 0
          ? "Es gab keine anderen angemeldeten Geraete."
          : `${result.revoked} ${result.revoked === 1 ? "anderes Geraet wurde" : "andere Geraete wurden"} abgemeldet.`,
      );
      await load();
    } catch {
      setError("Die anderen Geraete konnten nicht abgemeldet werden.");
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const others = sessions.filter((session) => !session.current);

  return (
    <section className="flex flex-col gap-4" data-testid="sessions-panel">
      <header className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Angemeldete Geraete</h2>
        <p className="text-sm text-jarvis-muted">
          Hier siehst du jedes Geraet, das gerade bei SIMO angemeldet ist — und kannst es einzeln abmelden.
        </p>
        {policy && <p className="text-sm text-jarvis-muted">{describeIdleWindow(policy)}</p>}
      </header>

      {(error || notice) && (
        <div ref={meldungRef} className="flex flex-col gap-2">
          {error && (
            <div className="flex flex-wrap items-center gap-2">
              <p role="alert" className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
                {error}
              </p>
              {/*
                SIMO-SESSION-LIST-ERROR-RETRY-1 — der Fehlerzustand liess den
                Owner bisher ohne Ausweg stehen: nur ein manuelles Neuladen
                der Seite half. `load()` traegt bereits den vollstaendigen
                Wiederholungs-Pfad (setzt `loading`, holt neu ab, ersetzt oder
                erneuert `error`) — der Knopf ruft ihn nur erneut auf.
              */}
              <button
                type="button"
                // 44 px Mindesthoehe wie die uebrigen Knoepfe der Ansicht.
                className="min-h-[44px] shrink-0 rounded-md border border-jarvis-border px-4 text-sm hover:border-jarvis-accent disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-jarvis-accent/70 focus-visible:ring-offset-1 focus-visible:ring-offset-jarvis-bg"
                disabled={loading}
                onClick={() => void load()}
              >
                {loading ? "Wird erneut versucht …" : "Erneut versuchen"}
              </button>
            </div>
          )}
          {notice && (
            <p role="status" className="rounded-md bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
              {notice}
            </p>
          )}
        </div>
      )}

      {loading && <p className="text-sm text-jarvis-muted">Geraeteliste wird geladen …</p>}

      {/*
        `!error` ist hier die eigentliche Aussage: eine leere Liste nach einem
        fehlgeschlagenen Abruf bedeutet NICHT, dass kein Geraet angemeldet ist —
        sie bedeutet, dass wir es nicht wissen. Ohne diese Bedingung stand im
        Fehlerfall woertlich „Die Geraeteliste konnte nicht geladen werden."
        direkt ueber „Es ist gerade kein Geraet angemeldet." Genau die falsche
        Entwarnung an genau der Stelle, an der sie am meisten schadet.
      */}
      {!loading && !error && sessions.length === 0 && (
        <p className="text-sm text-jarvis-muted">Es ist gerade kein Geraet angemeldet.</p>
      )}

      <ul className="flex flex-col gap-3">
        {sessions.map((session) => {
          const status = sessionStatus(session);
          return (
            <li
              key={session.id}
              data-testid="session-row"
              data-session-current={session.current ? "true" : "false"}
              // `min-w-0` ist hier kein Schmuck: ohne das sprengt eine lange
              // Clientbezeichnung die Zeile und erzeugt horizontales Scrollen.
              className="flex min-w-0 flex-col gap-2 rounded-lg border border-jarvis-border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium">{session.clientLabel ?? CHANNEL_LABELS[session.channel]}</span>
                  <StatusBadge status={status} current={session.current} />
                </div>
                <p className="text-xs text-jarvis-muted">
                  {CHANNEL_LABELS[session.channel]} · angemeldet {formatSessionTime(session.createdAt)}
                </p>
                <p className="text-xs text-jarvis-muted">
                  zuletzt benutzt {formatSessionTime(session.lastSeenAt)} · laeuft ab {formatSessionTime(session.expiresAt)}
                </p>
                {/*
                  Die Kennung bleibt bewusst die leiseste Zeile der Karte, muss
                  aber lesbar sein. Bei `/70` lag der gemessene Kontrast gegen
                  den App-Hintergrund bei 4.34:1 und damit unter den 4.5:1, die
                  fuer Text dieser Groesse gelten; `/80` ergibt 5.38:1 und bleibt
                  weiterhin deutlich unter Metazeile (7.94:1) und Name (18.15:1).
                */}
                <p className="text-[11px] text-jarvis-muted/80">Kennung {shortSessionId(session.id)}</p>
              </div>
              {!session.current && (
                <button
                  type="button"
                  // 44 px Mindesthoehe: Pflicht fuer Touch, nicht Geschmack.
                  className="min-h-[44px] shrink-0 rounded-md border border-jarvis-border px-4 text-sm hover:border-jarvis-accent disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-jarvis-accent/70 focus-visible:ring-offset-1 focus-visible:ring-offset-jarvis-bg"
                  disabled={busyId !== null}
                  onClick={() => void revokeOne(session)}
                  // SIMO-SESSION-LIST-A11Y-BUTTON-NAMES-1 — visuell trennt die
                  // Kartenumrandung die Zeilen, fuer Screenreader/Tastatur
                  // klangen aber alle drei Knoepfe identisch ("Dieses Geraet
                  // abmelden" dreimal). Der zugaengliche Name bekommt deshalb
                  // die Geraetebezeichnung plus die ohnehin sichtbare gekuerzte
                  // Kennung dazu — die sichtbare Beschriftung bleibt kurz und
                  // unveraendert. Bewusst NICHT die volle Session-ID: sie ist
                  // ein interner Bezeichner (siehe Dateikopf).
                  aria-label={`Dieses Geraet abmelden – ${session.clientLabel ?? CHANNEL_LABELS[session.channel]} (Kennung ${shortSessionId(session.id)})`}
                >
                  {busyId === session.id ? "Wird abgemeldet …" : "Dieses Geraet abmelden"}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {others.length > 0 && (
        <div className="flex flex-col gap-2 rounded-lg border border-jarvis-border p-3">
          <p className="text-sm">
            Alle anderen Geraete werden abgemeldet. <span className="text-jarvis-muted">Dieses Geraet bleibt angemeldet.</span>
          </p>
          <button
            type="button"
            className="min-h-[44px] self-start rounded-md border border-jarvis-border px-4 text-sm hover:border-jarvis-accent disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-jarvis-accent/70 focus-visible:ring-offset-1 focus-visible:ring-offset-jarvis-bg"
            disabled={busyId !== null}
            onClick={() => void revokeOthers()}
          >
            {busyId === "others" ? "Wird abgemeldet …" : `Alle anderen abmelden (${others.length})`}
          </button>
        </div>
      )}
    </section>
  );
}
